import Decimal from 'decimal.js'
import {
  DeepReadonly,
  GeckoBinSize,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoMeasurementUnit,
} from 'types'

// -----------------------------------------------------------------------
// Type Definitions
/**
 * A class to house the data-values of a data layer (CDF).
 */
export interface GeckoBinnedDataBuffer {
  values: Readonly<Float32Array>
  unit: GeckoMeasurementUnit
  _xBinCount: number
  _xGeometryStart: GeckoMeasurement
  _yGeometryStart: GeckoMeasurement
  _binSize: GeckoBinSize
}

/**
 * A serialize-able version of GeckoBinnedDataBuffer, where all properties are read-only
 */
export type GeckoBinnedDataBufferSerializable = DeepReadonly<{
  values: Float32Array
  unit: string
  x_bin_count: number
  x_geometry_start: GeckoMeasurementJson
  y_geometry_start: GeckoMeasurementJson
  bin_size: {
    bin_size_width: GeckoMeasurementJson
    bin_size_height: GeckoMeasurementJson
  }
}>

// -----------------------------------------------------------------------
// Factory Methods

/**
 * A factory method to create a GeckoBinnedDataBuffer using a Float32Array and a CDF.
 *
 * @param values - a Float32Array of data-layer values
 * @param cdf    - the cdf who's data this is
 */
export function createBinnedDataBufferFromData(
  values: Readonly<Float32Array>,
  cdf: GeckoComponentDataFrame
): GeckoBinnedDataBuffer {
  return {
    values,
    unit: cdf.data.params.measurement_unit,
    _xBinCount: cdf.data.binning.x_bin_count,
    _xGeometryStart: cdf.data.geometry.x.start,
    _yGeometryStart: cdf.data.geometry.y.start,
    _binSize: cdf.data.binning.bin_size,
  }
}

/**
 * A factory method to create a GeckoBinnedDataBuffer from a serialized GeckoBinnedDataBuffer.
 * This is mostly used for passing GeckoBinnedDataBuffers to and from web-workers.
 *
 * @param serialized - a serialized GeckoBinnedDataBuffer
 * @constructor
 */
export function createBinnedDataBufferFromSerialized(
  serialized: GeckoBinnedDataBufferSerializable
): GeckoBinnedDataBuffer {
  const binWidth = GeckoMeasurement.CreateFromJSON(
    serialized.bin_size.bin_size_width
  )
  const binHeight = GeckoMeasurement.CreateFromJSON(
    serialized.bin_size.bin_size_height
  )

  return {
    values: serialized.values,
    unit: GeckoMeasurementUnit.GetOrCreate(serialized.unit),
    _xBinCount: serialized.x_bin_count,
    _xGeometryStart: GeckoMeasurement.CreateFromJSON(
      serialized.x_geometry_start
    ),
    _yGeometryStart: GeckoMeasurement.CreateFromJSON(
      serialized.y_geometry_start
    ),
    _binSize: GeckoBinSize.GetOrCreate(
      binWidth.toNumber(binWidth.unit),
      binWidth.unit,
      binHeight.toNumber(binHeight.unit),
      binHeight.unit
    ),
  }
}

// -----------------------------------------------------------------------
// Utility Functions

/**
 * Get a serializable version of a given GeckoBinnedDataBuffer
 */
export function serializeBinnedDataBuffer(
  binnedDataBuffer: GeckoBinnedDataBuffer
): GeckoBinnedDataBufferSerializable {
  return {
    values: binnedDataBuffer.values,
    unit: binnedDataBuffer.unit.id,
    x_bin_count: binnedDataBuffer._xBinCount,
    x_geometry_start: binnedDataBuffer._xGeometryStart.toJSON(),
    y_geometry_start: binnedDataBuffer._yGeometryStart.toJSON(),
    bin_size: {
      bin_size_width: binnedDataBuffer._binSize.width.toJSON(),
      bin_size_height: binnedDataBuffer._binSize.height.toJSON(),
    },
  }
}

/**
 * Get a numerical value in the buffer's data, indexing as if the data was a 2d-grid
 *
 * @param buffer - the buffer to grab a data-value from
 * @param xIndex - an index of the x-column to grab a data-value from
 * @param yIndex - an index of the y-row to grab a data-value from
 */
export function atBuffer2DIndex(
  buffer: Pick<GeckoBinnedDataBuffer, 'values' | '_xBinCount'>,
  xIndex: number,
  yIndex: number
): number | undefined {
  const flattenedIndex = yIndex * buffer._xBinCount + xIndex
  return buffer.values.at(flattenedIndex)
}

/**
 * Get the numerical value for a bin a given coordinate in a data buffer.
 *
 * @param buffer - the buffer to grab a data-value from
 * @param coord - the coordinate for the bin to get the value for (using *centroid binning*)
 */
export function atBufferCoord(
  buffer: Pick<
    GeckoBinnedDataBuffer,
    '_binSize' | '_xGeometryStart' | '_yGeometryStart' | 'values' | '_xBinCount'
  >,
  coord: GeckoComponentCoords
): number | undefined {
  const xPositionDecimal = new Decimal(
    coord.x.toNumber(buffer._binSize.width.unit)
  )
  const xGeometryStartDecimal = new Decimal(
    buffer._xGeometryStart.toNumber(buffer._binSize.width.unit)
  )
  const binWidthDecimal = new Decimal(
    buffer._binSize.width.toNumber(buffer._binSize.width.unit)
  )

  const xIndex = xPositionDecimal
    .minus(binWidthDecimal.div(2))
    .minus(xGeometryStartDecimal)
    .div(binWidthDecimal)
    .toNumber()

  const yPositionDecimal = new Decimal(
    coord.y.toNumber(buffer._binSize.height.unit)
  )
  const yGeometryStartDecimal = new Decimal(
    buffer._yGeometryStart.toNumber(buffer._binSize.height.unit)
  )
  const binHeightDecimal = new Decimal(
    buffer._binSize.height.toNumber(buffer._binSize.height.unit)
  )

  const yIndex = yPositionDecimal
    .minus(binHeightDecimal.div(2))
    .minus(yGeometryStartDecimal)
    .div(binHeightDecimal)
    .toNumber()

  return atBuffer2DIndex(buffer, xIndex, yIndex)
}

// -----------------------------------------------------------------------
// Static Instances
export const EMPTY_BINNED_DATA_BUFFER: GeckoBinnedDataBuffer = {
  values: new Float32Array(),
  unit: GeckoMeasurementUnit.None,
  _xBinCount: 0,
  _xGeometryStart: GeckoMeasurement.Zero(),
  _yGeometryStart: GeckoMeasurement.Zero(),
  _binSize: GeckoBinSize.TwelveInchBins,
}
